import { Pipe, LOCALE_ID } from '@angular/core';
import { formatNumber, formatDate } from '@angular/common';

export interface LogItemParts {
  label: string;
  value: string;
}

export class FormatData {
  static readonly LOG_CHANGE_ARROW = '➞';

  private static readonly ACTION_TASK_FIELDS = [
    'jobAction',
    'jobActions',
    'todo',
    'status',
  ];

  constructor() {

  }

  static formatCurrency(currency:any) {
    if (typeof currency == 'string') {
      currency = currency.replace(/,/g, '')
    }
    let tmpValue = parseFloat(currency);
    let value = (isNaN(tmpValue)) ? 0 : tmpValue;
    return formatNumber(value, 'en-US', '1.2-2');
  }

  static formatFloat(value:any) {
    if (typeof value == 'string') {
      value = value.replace(/,/g, '')
    }
    let tmpValue = parseFloat(value);
    value = (isNaN(tmpValue)) ? 0 : tmpValue;
    return value;
  }

  static formatLogItem(item?: string): string {
    const parts = FormatData.parseLogItem(item);
    if (parts.value) {
      return `${parts.label} ${FormatData.LOG_CHANGE_ARROW} ${parts.value}`;
    }
    return parts.label;
  }

  static parseLogItem(item?: string): LogItemParts {
    const raw = String(item ?? '').trim();
    if (!raw) {
      return { label: '', value: '' };
    }
    switch (raw) {
      case 'ORDER FOR RETURN':
        return { label: 'RETURNING', value: '' };
      case 'FRAME RETURNED':
        return { label: 'AWAITING CREDIT', value: '' };
      case 'ORDER RETURNED':
        return { label: 'RETURNED', value: '' };
      case 'FRAME RECEIVED':
        return { label: 'RECEIVED', value: '' };
      case 'ORDERED FRAME':
        return { label: 'ORDERED', value: '' };
      case 'JOB CREATED':
        return { label: 'JOB CREATED', value: '' };
      case 'QUOTE CREATED':
        return { label: 'QUOTE CREATED', value: '' };
      case 'CONVERTED TO ORDER':
        return { label: 'CONVERTED TO ORDER', value: '' };
      case 'HOLD':
        return { label: 'HOLD/RX', value: '' };
      case 'CALL':
        return { label: 'CALL FOR RX', value: '' };
      case 'CALLED FOR RX':
        return { label: 'CALLED FOR RX', value: '' };
      case 'RX RECEIVED':
        return { label: 'RX RECEIVED', value: '' };
      case 'PART RECEIVED':
        return { label: 'PART RECEIVED', value: '' };
      case 'REPAIR RECEIVED':
        return { label: 'REPAIR RECEIVED', value: '' };
      case 'LENSES RECEIVED':
        return { label: 'LENSES RECEIVED', value: '' };
      case 'ORDER':
        return { label: 'SENT TO', value: 'TO-DO' };
      case 'SHIPPED':
        return { label: 'SHIPPED', value: '' };
      case 'SENT FOR REPAIR':
        return { label: 'SENT FOR REPAIR', value: '' };
      case 'REPAIR / ENCLOSED':
        return { label: 'REPAIR / ENCLOSED', value: '' };
      case 'REPAIR':
        return { label: 'REPAIR', value: '' };
      case 'DUPLICATE':
        return { label: 'DUPLICATE', value: '' };
      case 'EXCHANGE':
        return { label: 'EXCHANGE', value: '' };
      case 'NEW RX':
        return { label: 'NEW RX', value: '' };
      case 'RE-DO':
        return { label: 'RE-DO', value: '' };
      case 'RETURN':
        return { label: 'RETURN', value: '' };
      case 'Invoice emailed to customer':
        return { label: 'Invoice emailed to customer', value: '' };
      case 'DISPENSED':
        return { label: 'DISPENSED', value: '' };
      default:
        break;
    }
    if (
      /^DUPLICATE FROM /i.test(raw) ||
      /^REPAIR FROM /i.test(raw) ||
      /^EXCHANGE FROM /i.test(raw) ||
      /^NEW RX FROM /i.test(raw) ||
      /^RE-DO FROM /i.test(raw) ||
      /^RETURN FROM /i.test(raw)
    ) {
      return { label: raw, value: '' };
    }
    const dirty = raw.match(/^([^:]+):\s*(.*)$/);
    if (dirty) {
      const key = dirty[1].trim();
      const label = FormatData.dirtyFieldLabel(key);
      const value = String(dirty[2] ?? '').trim();
      if (label) {
        if (value && FormatData.ACTION_TASK_FIELDS.includes(key)) {
          return { label, value };
        }
        return { label: value ? `${label} ${value}` : label, value: '' };
      }
    }
    return { label: raw, value: '' };
  }

  static dirtyFieldLabel(key: string): string {
    switch (key) {
      case 'physicalStatus':
        return 'FRAME STATUS CHANGED TO';
      case 'status':
        return 'JOB STATUS CHANGED TO';
      case 'jobAction':
        return 'JOB ACTION SELECTED';
      case 'jobActions':
        return 'JOB ACTIONS SELECTED';
      case 'lensStatus':
        return 'LENS STATUS SET TO';
      case 'eta':
        return 'ETA SET TO';
      case 'todo':
        return 'SENT TO';
      case 'followUpDate':
        return 'FOLLOW-UP DATE SET TO';
      case 'referenceNumber':
        return 'VENDOR REF. #';
      case 'activityStatus':
        return 'ACTIVITY ADDED';
      case 'fulfillment':
        return 'FULFILLMENT SET TO';
      case 'carrier':
        return 'CARRIER SET TO';
      case 'trackingNumber':
        return 'TRACKING #';
      case 'labVendor':
        return 'LAB / VENDOR ENTERED';
      case 'rxStatus':
        return 'RX STATUS SET TO';
      case 'color':
        return 'FRAME COLOR CHANGED TO';
      case 'model':
        return 'FRAME MODEL CHANGED TO';
      case 'size':
        return 'FRAME SIZE CHANGED TO';
      case 'invoiceNumber':
        return 'INVOICE NUMBER ASSIGNED';
      case 'vendor':
        return 'VENDOR/LAB ENTERED';
      case 'frameOnly':
        return 'FRAME ONLY';
      default:
        return '';
    }
  }

  static invoiceFamilyRoot(invoiceNumber?: string): string {
    return String(invoiceNumber || '')
      .trim()
      .replace(/-(RX|FX|RT|V|C|R|X)\d*$/i, '');
  }
}
